import * as Popover from '@radix-ui/react-popover';
import { useMemo, useState, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { EMOJI_CATEGORIES, recentEmoji, rememberEmoji, searchEmoji } from '../../lib/emoji';

function Grid({ emoji, onPick }: { emoji: { e: string; n: string }[]; onPick: (e: string) => void }) {
  return (
    <div className="grid grid-cols-8 gap-0.5">
      {emoji.map((x) => (
        <button
          key={x.n + x.e}
          type="button"
          title={`:${x.n}:`}
          aria-label={x.n.replace(/_/g, ' ')}
          onClick={() => onPick(x.e)}
          className="flex size-9 items-center justify-center rounded-md text-2xl leading-none hover:bg-hover"
        >
          {x.e}
        </button>
      ))}
    </div>
  );
}

export function EmojiPanel({ onPick }: { onPick: (emoji: string) => void }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const recent = useMemo(() => recentEmoji().map((e) => ({ e, n: 'recent' })), []);
  const pick = (e: string) => {
    rememberEmoji(e);
    onPick(e);
  };
  const results = query ? searchEmoji(query, 80) : null;
  const shown = category ? EMOJI_CATEGORIES.filter((c) => c.name === category) : EMOJI_CATEGORIES;

  return (
    <div className="flex h-[360px] w-[320px] flex-col">
      <div className="relative p-2">
        <Search className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search emoji"
          aria-label="Search emoji"
          className="h-9 w-full rounded-md border border-line bg-input pl-8 pr-2 text-sm text-fg outline-none focus:border-accent"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && results?.[0]) pick(results[0].e);
          }}
        />
      </div>
      {!query && (
        <div className="flex gap-1 overflow-x-auto px-2 pb-2 scroll-thin" role="tablist" aria-label="Emoji categories">
          <button type="button" role="tab" aria-selected={!category} onClick={() => setCategory(null)} className={`shrink-0 rounded px-2 py-0.5 text-xs ${!category ? 'bg-accent text-accent-fg' : 'text-muted hover:bg-hover'}`}>
            All
          </button>
          {EMOJI_CATEGORIES.map((c) => (
            <button key={c.name} type="button" role="tab" aria-selected={category === c.name} onClick={() => setCategory(c.name)} className={`shrink-0 rounded px-2 py-0.5 text-xs ${category === c.name ? 'bg-accent text-accent-fg' : 'text-muted hover:bg-hover'}`}>
              {c.name}
            </button>
          ))}
        </div>
      )}
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {results ? (
          results.length ? <Grid emoji={results} onPick={pick} /> : <p className="p-4 text-center text-sm text-muted">No emoji match "{query}".</p>
        ) : (
          <>
            {!category && recent.length > 0 && (
              <section className="mb-2">
                <h3 className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-faint">Recent</h3>
                <Grid emoji={recent} onPick={pick} />
              </section>
            )}
            {shown.map((c) => (
              <section key={c.name} className="mb-2">
                <h3 className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-faint">{c.name}</h3>
                <Grid emoji={c.emoji} onPick={pick} />
              </section>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export function EmojiPicker({ children, onPick, open, onOpenChange, side = 'top', align = 'end' }: { children: ReactNode; onPick: (e: string) => void; open?: boolean; onOpenChange?: (o: boolean) => void; side?: 'top' | 'bottom' | 'left' | 'right'; align?: 'start' | 'end' | 'center' }) {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content side={side} align={align} sideOffset={8} collisionPadding={12} className="animate-pop z-[75] rounded-xl border border-line bg-overlay shadow-pop">
          <EmojiPanel onPick={onPick} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
